import React from "react";
import PropTypes from "prop-types";
import { Day } from "date-fns";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";
import DayButton from "./DayButton";
import { generateCalendarData } from "./utils/calendar";

import styles from "./DateRangePicker.scss";

export type CalendarBodyProps = {
  firstDayOfWeek?: Day;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  previewKeyboardDate?: Date;
  previewDate?: Date;
  selectedCalendarMonth: number;
  selectedCalendarYear: number;
  firstRender: boolean;
};

const defaultProps = {
  firstDayOfWeek: 1,
  selectedStartDate: undefined,
  selectedEndDate: undefined,
  previewKeyboardDate: undefined,
  previewDate: undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  CalendarBodyProps,
  typeof defaultProps
> = defaultProps;

const CalendarBody: ((props: CalendarBodyProps) => JSX.Element) &
  ReactComponentStaticProps<CalendarBodyProps> = ({
  firstDayOfWeek = defaultProps.firstDayOfWeek,
  selectedStartDate,
  selectedEndDate,
  previewDate,
  previewKeyboardDate,
  selectedCalendarMonth,
  selectedCalendarYear,
  firstRender,
}: CalendarBodyProps): JSX.Element => {
  const data = generateCalendarData(
    selectedCalendarYear,
    selectedCalendarMonth,
    firstDayOfWeek
  );

  return (
    <tbody>
      {data.map((weekRow, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={`week-row-${index}`} className={styles["calendar-date-cell"]}>
          {weekRow.map((day) => {
            if (!day.isCurrentMonthDate) {
              return <td key={day.date.toDateString()} />;
            }

            return (
              <td
                key={day.date.toDateString()}
                className={styles["calendar-date-cell"]}
              >
                <DayButton
                  date={day.date}
                  isFirstDayOfMonth={day.isFirstDayOfMonth}
                  isLastDayOfMonth={day.isLastDayOfMonth}
                  isFirstDayOfWeek={day.isFirstDayOfWeek}
                  isLastDayOfWeek={day.isLastDayOfWeek}
                  selectedStartDate={selectedStartDate}
                  selectedEndDate={selectedEndDate}
                  previewKeyboardDate={previewKeyboardDate}
                  previewDate={previewDate}
                  firstRender={firstRender}
                />
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

CalendarBody.propTypes = {
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  selectedStartDate: PropTypes.instanceOf(Date),
  selectedEndDate: PropTypes.instanceOf(Date),
  previewKeyboardDate: PropTypes.instanceOf(Date),
  previewDate: PropTypes.instanceOf(Date),
  selectedCalendarMonth: PropTypes.number.isRequired,
  selectedCalendarYear: PropTypes.number.isRequired,
  firstRender: PropTypes.bool.isRequired,
};

CalendarBody.defaultProps = validatedDefaultProps;

CalendarBody.displayName = "DateRangePicker.CalendarBody";

export default CalendarBody;
